/**
 * Messages des groupes WhatsApp des cabinets — le vocabulaire commun.
 *
 * `MessageGroupeEntrant` est la SEULE forme que le reste du CRM connaît d'un
 * message de groupe. La passerelle (WAHA) a sa propre forme d'événement ; elle
 * est traduite à l'entrée par un adaptateur unique, et rien en aval ne la voit.
 *
 * A4 : aucun nom de fichier de pièce jointe n'entre ici. Un document appelé
 * « DUPONT_Jean.pdf » porte un nom de patient : seul le TYPE de pièce voyage.
 */
import { z } from "zod";

export const MessageGroupeEntrantSchema = z.object({
  /** identifiant WhatsApp du message : sert d'unique, un rejeu ne duplique rien */
  waMessageId: z.string().min(1),
  /** identifiant du groupe, toujours terminé par @g.us */
  groupeWaId: z.string().min(1),
  /** expéditeur tel que WhatsApp le donne — peut être un @lid sans numéro */
  auteurId: z.string().min(1),
  auteurNom: z.string().nullable(),
  /** texte ou légende ; chaîne vide s'il n'y en a pas */
  texte: z.string(),
  /** « image », « document », « audio », « video » ou « autre » ; null sans pièce */
  mediaType: z.string().nullable(),
  /** adresse de téléchargement côté passerelle ; jamais un nom de fichier */
  mediaUrl: z.string().nullable(),
  /** ISO 8601 */
  envoyeLe: z.string().datetime(),
});
export type MessageGroupeEntrant = z.infer<typeof MessageGroupeEntrantSchema>;

export type EtatPasserelle = "debranche" | "qr_a_scanner" | "branche" | "deconnecte" | "bloque";

/** Où en est le rangement patient d'un message de groupe. */
export type RangementGroupe = "a_classer" | "auto_a_valider" | "valide" | "cabinet_seul";

/**
 * Un message de groupe tel que l'écran le reçoit. Jamais l'adresse interne de
 * la passerelle (`media_url`) : seul le statut de la pièce voyage.
 */
export interface MessageGroupeDto {
  id: string;
  auteurNom: string | null;
  texte: string;
  mediaType: string | null;
  mediaStatut: string | null;
  /**
   * Vrai SEULEMENT quand la pièce est rangée chez pCloud (`media_statut = 'range'`) :
   * l'écran peut alors la lire par `GET /api/v1/whatsapp-groupes/messages/:id/media`.
   * La référence pCloud, elle, ne sort jamais.
   */
  mediaDisponible: boolean;
  envoyeLe: string;
  rangement: RangementGroupe;
  patientNom: string | null;
}

/** §17.5 : le compteur « À classer » EST `items.length` de cette réponse. */
export interface MessagesGroupeResponse {
  items: MessageGroupeDto[];
}

export interface GroupeWaDto {
  groupeWaId: string;
  nom: string;
  idVevidental: string | null;
  actif: boolean;
  relieLe: string | null;
  reliePar: string | null;
}

export interface GroupesWaResponse {
  items: GroupeWaDto[];
}

/** La passerelle INTERROGÉE au moment de la lecture (même règle que l'écran Santé). */
export interface MesurePasserelleDto {
  etat: "ok" | "degrade" | "hs";
  detail: string;
}

export interface EtatGroupesResponse {
  /** dernier état NOTÉ par un événement reçu — un souvenir, pas une mesure */
  etat: EtatPasserelle;
  raison: string | null;
  dernierEvenementLe: string | null;
  /** l'état MESURÉ : c'est lui que l'écran affiche (C3) */
  mesure: MesurePasserelleDto;
  /** interrupteur `whatsapp_groupes_ingest` : faux = rien n'est gardé */
  ingestActif: boolean;
}

/** Réponse de « Actualiser la liste » : combien de groupes la passerelle a rendus. */
export interface ActualisationGroupesResponse {
  connus: number;
}

/** Corps de `corriger` : un patient du cabinet, ou « cabinet seul ». */
export const CorrectionRangementSchema = z.union([
  z.object({ patientNom: z.string().trim().min(1).max(200) }).strict(),
  z.object({ cabinetSeul: z.literal(true) }).strict(),
]);
export type CorrectionRangement = z.infer<typeof CorrectionRangementSchema>;

/** Corps de `relier` : l'identifiant Vevidental du cabinet (chiffres seulement). */
export const LiaisonGroupeSchema = z.object({ idVevidental: z.string().regex(/^[0-9]+$/) }).strict();
